import type { DealTransformed } from "@/lib/schema/deal-schema";
import {
  ClipboardList,
  Globe,
  LineChart,
  Linkedin,
  Loader2,
  Search,
  ShieldAlert,
  Target,
  TrendingUp,
} from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { linkedInScraperApi } from "@/api/submission-api";
import { getReports } from "@/api/gen-api";
import { toast } from "sonner";
import { aiApi } from "@/api/ai-api";
import { activateDeal, swapPremiumDeal } from "@/api/deals-api";
import { useWorkspaceStore } from "@/store/workspace-store";
import WebsiteAnalyticsCard from "@/components/deal-detail/website-analytics/website-analytics-card";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { getErrorMessage } from "@/lib/api-error";
import { ScoreBar } from "./score-bar";
import { VersionSelector, type VersionedItem } from "./version-selector";
import { ReportActionsMenu } from "./report-actions-menu";
import { PremiumActivationDialog } from "@/components/modals/premium-activation-dialog";
import { PremiumSwapModal } from "@/components/modals/premium-swap-modal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AnalysisItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  isPremium: boolean;
  dataKey: string;
  /** report_status key if it differs from dataKey */
  statusKey?: string;
  /** Report view gates behind currentDeal.aiTracked (PremiumActivationCard) */
  requiresActivation: boolean;
  /**
   * Skip the version selector for this item — valuations uses its own
   * `useValuations` hook for versions, so the `aiGenerations` array isn't
   * the source of truth.
   */
  skipVersionSelector?: boolean;
  /**
   * Override for "is data present?" when the data doesn't live under
   * `currentDeal.aiGenerations[dataKey]` — e.g. Questionnaire reads
   * `currentDeal.questionnaire`.
   */
  hasDataOverride?: (deal: DealTransformed) => boolean;
  /**
   * Items that aren't really "generated" reports (Questionnaire) shouldn't
   * surface the "Completed" sub-label or a score row when openable —
   * the description stays put so the card reads as a static link.
   */
  hideCompletedLabel?: boolean;
  /**
   * If returned, the card swaps the dark "Run" button for an outline
   * call-to-action that navigates the user elsewhere to satisfy the
   * pre-condition (e.g. LinkedIn needs an approved URL on Profile).
   * Returning `null` keeps the standard Run button.
   */
  getRunBlocker?: (
    deal: DealTransformed,
  ) => { ctaLabel: string; ctaTab: string; ctaFieldId?: string } | null;
}


const analysisItems: AnalysisItem[] = [
  {
    id: "deal-fit",
    title: "Deal Fit",
    description:
      "Evaluates alignment with fund thesis and investment criteria",
    icon: Target,
    isPremium: false,
    dataKey: "deal_fit",
    requiresActivation: false,
  },
  {
    id: "gap-analysis",
    title: "Gap Analysis",
    description:
      "Identifies missing information and areas requiring further diligence",
    icon: Search,
    isPremium: true,
    dataKey: "gap_analysis",
    requiresActivation: true,
  },
  {
    id: "insights",
    title: "Insights",
    description: "AI-generated business insights and findings",
    icon: LineChart,
    isPremium: true,
    dataKey: "insights",
    requiresActivation: true,
  },
  {
    id: "market-intel",
    title: "Market Intelligence",
    description:
      "Market research, competitive landscape, and industry trends",
    icon: TrendingUp,
    isPremium: false,
    dataKey: "market_research",
    requiresActivation: false,
  },
  {
    id: "business-risk",
    title: "Business Risk",
    description: "Risk assessment and scoring",
    icon: ShieldAlert,
    isPremium: true,
    dataKey: "startupscores",
    statusKey: "startupscore",
    requiresActivation: true,
  },
  // Valuations hidden for now
  // {
  //   id: "valuations",
  //   title: "Valuations",
  //   description: "Valuation analysis and benchmarking",
  //   icon: TrendingUp,
  //   isPremium: true,
  //   dataKey: "valuations",
  //   requiresActivation: true,
  //   skipVersionSelector: true,
  // },
  // Source-data reports (formerly under the legacy per-deal sidebar)
  // — wired into the same Run/Open/Versions/More pattern so the panel
  // toolbar handles regenerate + delete consistently with the AI items
  // above.
  {
    id: "website-info",
    title: "Website Info",
    description: "Scraped website content, summaries, and metadata",
    icon: Globe,
    isPremium: false,
    dataKey: "website_summary_v2",
    statusKey: "website_summary_v2",
    requiresActivation: false,
  },
  {
    id: "linkedin-info",
    title: "LinkedIn Info",
    description: "LinkedIn-scraped company profile and team data",
    icon: Linkedin,
    isPremium: false,
    dataKey: "linkedInInfo",
    statusKey: "linkedin_company_scrape",
    requiresActivation: false,
    // Scrape needs an approved LinkedIn URL on the deal. When one
    // isn't available, hide Run and surface a Profile-tab CTA instead
    // so the user fixes the pre-condition rather than firing a
    // request that would just toast an error.
    getRunBlocker: (deal) => {
      const linkedInField = deal?.dealInfo.deal?.find(
        (f) => f.field_name === "linkedIn",
      );
      const isPending = linkedInField?.field_state === "pending";
      const value = (linkedInField?.field_value || "") as string;
      const hasUsableValue =
        !isPending && !!value && value.trim().length > 0;
      if (hasUsableValue) return null;
      return {
        ctaLabel: isPending
          ? "Approve LinkedIn URL"
          : "Add LinkedIn URL",
        ctaTab: "profile",
        ctaFieldId: "field-linkedIn",
      };
    },
  },
  {
    id: "questionnaire",
    title: "Questionnaire",
    description: "Founder-submitted application questionnaire responses",
    icon: ClipboardList,
    isPremium: false,
    // Questionnaire isn't a "generated" report — the questions exist
    // as soon as the workspace defines them. Always offer Open (no
    // Run, no toolbar Generate, no "Completed" label).
    dataKey: "__questionnaire__",
    requiresActivation: false,
    skipVersionSelector: true,
    hasDataOverride: () => true,
    hideCompletedLabel: true,
  },
];

interface AnalysisTabProps {
  currentDeal: DealTransformed;
  reportStatus: unknown;
  onOpenInPanel: (type: string, title: string, versionId?: string) => void;
  /**
   * Switches the deal-detail page to a sibling tab. Used when a card's
   * Run prerequisites (e.g. LinkedIn URL approved) aren't met — we send
   * the user to the Profile tab to fix the prerequisite.
   */
  onNavigateToTab?: (tab: string, targetId?: string) => void;
}

function AnalysisTab({
  currentDeal,
  reportStatus,
  onOpenInPanel,
  onNavigateToTab,
}: AnalysisTabProps) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { updateDeal } = useCurrentDealStore();
  const { dealStats, fetchDealStats } = useDealStatsStore();
  const { quota, orgId } = useOrgQuota();
  const queryClient = useQueryClient();

  // The deal-fetch BE projection (`fetch_single_deal`) does NOT include
  // `aiGenerations.website_summary_v2` or `aiGenerations.linkedInInfo`,
  // so reading them straight off `currentDeal` would always be empty
  // and the card would falsely show "Run" for reports that already
  // exist. Hydrate both via `/get_reports` and merge them back into
  // the in-memory deal so every consumer (cards, panel viewer, version
  // dropdown) sees the same data.
  const sourceDataReports = useQueries({
    queries: [
      {
        queryKey: [
          "source-report",
          "website_summary_v2",
          currentDeal?.id,
          activeWorkspaceId,
        ],
        queryFn: async () => {
          if (!currentDeal?.id || !activeWorkspaceId) return [];
          const resp = await getReports(
            currentDeal.id,
            activeWorkspaceId,
            "website_summary_v2",
          );
          return (resp?.data?.website_summary_v2 ?? []) as Array<unknown>;
        },
        enabled: !!currentDeal?.id && !!activeWorkspaceId,
        staleTime: 60_000,
      },
      {
        queryKey: [
          "source-report",
          "linkedInInfo",
          currentDeal?.id,
          activeWorkspaceId,
        ],
        queryFn: async () => {
          if (!currentDeal?.id || !activeWorkspaceId) return [];
          const resp = await getReports(
            currentDeal.id,
            activeWorkspaceId,
            "linkedInInfo",
          );
          return (resp?.data?.linkedInInfo ?? []) as Array<unknown>;
        },
        enabled: !!currentDeal?.id && !!activeWorkspaceId,
        staleTime: 60_000,
      },
    ],
  });

  const websiteSummaryReports = sourceDataReports[0]?.data;
  const linkedInInfoReports = sourceDataReports[1]?.data;

  // Merge fetched reports into the in-memory deal so the card's
  // hasData check (and the panel viewer below) read the truth.
  useEffect(() => {
    const next: Record<string, unknown> = {};
    const current = currentDeal.aiGenerations as Record<string, unknown>;
    if (
      websiteSummaryReports &&
      JSON.stringify(websiteSummaryReports) !==
        JSON.stringify(current.website_summary_v2 ?? [])
    ) {
      next.website_summary_v2 = websiteSummaryReports;
    }
    if (
      linkedInInfoReports &&
      JSON.stringify(linkedInInfoReports) !==
        JSON.stringify(current.linkedInInfo ?? [])
    ) {
      next.linkedInInfo = linkedInInfoReports;
    }
    if (Object.keys(next).length === 0) return;
    updateDeal({
      aiGenerations: {
        ...currentDeal.aiGenerations,
        ...next,
      } as typeof currentDeal.aiGenerations,
    });
    // We intentionally only depend on the fetched arrays here. Including
    // currentDeal.aiGenerations would loop because we mutate it via
    // updateDeal. The JSON.stringify diff above prevents redundant writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteSummaryReports, linkedInInfoReports]);

  // Track which items the user just triggered — used to auto-open the panel
  // when generation completes.
  const pendingOpenRef = useRef<Set<string>>(new Set());
  const genBaselineRef = useRef<Record<string, string | undefined>>({});
  // Optimistic "pending" set driving the spinner state. We set this in the
  // mutation's onMutate so the spinner stays continuous from click until the
  // server's reportStatus shows the run as in_progress — without this, the
  // window between react-query flipping isPending=false and reportStatus
  // catching up renders the bare "Run" button for one frame.
  const [pendingItems, setPendingItems] = useState<Set<string>>(new Set());
  const markPending = (id: string) =>
    setPendingItems((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const clearPending = (id: string) =>
    setPendingItems((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  const [selectedVersionByItem, setSelectedVersionByItem] = useState<
    Record<string, string>
  >({});
  const [deleteTarget, setDeleteTarget] = useState<
    | {
        item: AnalysisItem;
        versionId: string;
      }
    | null
  >(null);
  const [runConfirmItem, setRunConfirmItem] = useState<AnalysisItem | null>(
    null,
  );
  const [swapItem, setSwapItem] = useState<AnalysisItem | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);

  const getAnalysisData = (dataKey: string) => {
    const gen = currentDeal.aiGenerations as Record<string, unknown>;
    if (!gen) return null;
    const data = gen[dataKey];
    if (Array.isArray(data)) {
      if (data.length === 0) return null;
      // Newest-first so the row summary reflects the latest version — the raw
      // array is oldest-first, and data[0] surfaced a stale first-version score
      // that didn't match the version dropdown (which uses getVersions below).
      return [...(data as VersionedItem[])].sort((a, b) => {
        const ad = a.created_on ? new Date(a.created_on).getTime() : 0;
        const bd = b.created_on ? new Date(b.created_on).getTime() : 0;
        return bd - ad;
      })[0];
    }
    if (data && typeof data === "object") return data;
    return null;
  };

  /** Versions array (sorted newest-first) for a given analysis item. */
  const getVersions = (item: AnalysisItem): VersionedItem[] => {
    if (item.skipVersionSelector) return [];
    const gen = currentDeal.aiGenerations as Record<string, unknown>;
    const raw = gen?.[item.dataKey];
    if (!Array.isArray(raw)) return [];
    return (raw as VersionedItem[])
      .filter((v) => v._id)
      .sort((a, b) => {
        const ad = a.created_on ? new Date(a.created_on).getTime() : 0;
        const bd = b.created_on ? new Date(b.created_on).getTime() : 0;
        return bd - ad;
      });
  };

  const getReportStatusValue = (item: AnalysisItem): string | undefined => {
    if (!reportStatus || typeof reportStatus !== "object") return undefined;
    const rs = reportStatus as Record<string, unknown>;
    const status = rs?.opportunityApplication as Record<string, unknown>;
    if (!status?.report_status) return undefined;
    const reports = status.report_status as Record<string, string>;
    return reports[item.statusKey ?? item.dataKey];
  };

  const isGenerating = (item: AnalysisItem): boolean => {
    const value = getReportStatusValue(item);
    return (
      value === "in_progress" ||
      value === "queued" ||
      value === "pending" ||
      (typeof value === "string" && value.startsWith("generating_"))
    );
  };

  type ScoreDisplay = {
    value: number;
    max: number;
    format: "percent" | "raw" | "number";
    labels: {
      high: { threshold: number; label: string };
      mid: { threshold: number; label: string };
      low: { label: string };
    };
  };

  const getScore = (dataKey: string): ScoreDisplay | null => {
    const data = getAnalysisData(dataKey);
    if (!data || typeof data !== "object") return null;
    const d = data as Record<string, unknown>;

    if (dataKey === "deal_fit" && d.deal_fit) {
      const fit = d.deal_fit as Record<string, unknown>;
      const overall = fit.overall as Record<string, unknown>;
      if (typeof overall?.score !== "number") return null;
      return {
        value: overall.score,
        max: 100,
        format: "number",
        labels: {
          high: { threshold: 70, label: "Strong" },
          mid: { threshold: 40, label: "Moderate" },
          // A deal violating one of the fund's hard exclusions is scored 0,
          // which would otherwise read as "Weak" — a near miss rather than
          // something the fund said it will never do.
          low: { label: overall.excluded ? "Excluded" : "Weak" },
        },
      };
    }
    if (dataKey === "insights" && Array.isArray(d.insights)) {
      let total = 0;
      let count = 0;
      for (const insight of d.insights as Array<Record<string, unknown>>) {
        const engines = insight?.engine_results;
        if (!Array.isArray(engines)) continue;
        for (const er of engines as Array<Record<string, unknown>>) {
          if (typeof er?.score === "number") {
            total += er.score;
            count++;
          }
        }
      }
      if (count === 0) return null;
      return {
        value: Number((total / count).toFixed(1)),
        max: 10,
        format: "raw",
        labels: {
          high: { threshold: 80, label: "Excellent" },
          mid: { threshold: 50, label: "Fair" },
          low: { label: "Critical" },
        },
      };
    }
    if (dataKey === "gap_analysis" && typeof d.gap_analysis_score === "number") {
      // Matches the report view: raw 0-10 score with Excellent/Fair/Critical labels.
      return {
        value: d.gap_analysis_score,
        max: 10,
        format: "raw",
        labels: {
          high: { threshold: 80, label: "Excellent" },
          mid: { threshold: 50, label: "Fair" },
          low: { label: "Critical" },
        },
      };
    }
    return null;
  };

  const triggerGeneration = async (item: AnalysisItem): Promise<unknown> => {
    if (!activeWorkspaceId) throw new Error("No workspace selected");
    const params = {
      companyId: currentDeal.id,
      opportunityId: activeWorkspaceId,
      applicationInfo: currentDeal.dealInfo.applicationInfo,
      applicationObject: currentDeal.dealInfo.applicationInfo,
    };
    switch (item.id) {
      case "deal-fit":
        return aiApi.getDealFit(params);
      case "gap-analysis":
        return aiApi.getGapAnalysis(params);
      case "insights":
        return aiApi.getInsights(params);
      case "market-intel":
        return aiApi.getMarketIntelligence(params);
      case "business-risk":
        return aiApi.getStartupScore(params);
      case "valuations":
        return aiApi.generateValuationsReport(
          activeWorkspaceId,
          currentDeal.id
        );
      case "website-info": {
        const websiteUrl = (currentDeal?.dealInfo.deal?.find(
          (f) => f.field_name === "company_website",
        )?.field_value || currentDeal?.overview.website) as string;
        if (!websiteUrl) {
          throw new Error("Add a website URL to the deal before analysing");
        }
        return aiApi.summarizeWebsite({
          companyId: currentDeal.id,
          opportunityId: activeWorkspaceId,
          url: websiteUrl,
        });
      }
      case "linkedin-info": {
        const linkedInField = currentDeal?.dealInfo.deal?.find(
          (f) => f.field_name === "linkedIn",
        );
        const linkedInUrl =
          linkedInField?.field_state !== "pending"
            ? ((linkedInField?.field_value || "") as string)
            : "";
        if (!linkedInUrl) {
          throw new Error("Add a LinkedIn URL to the deal before scraping");
        }
        return linkedInScraperApi.scrapeLinkedIn(
          linkedInUrl,
          currentDeal.id,
          activeWorkspaceId,
        );
      }
      case "questionnaire": {
        // No single batch endpoint — iterate per-question. Mutates the
        // local deal so the cards re-render once each call resolves.
        const questions = currentDeal.questionnaire || [];
        for (const qa of questions) {
          const response = await aiApi.getQuestionnaireAnswer({
            companyId: currentDeal.id,
            opportunityId: activeWorkspaceId,
            question: qa.question,
            applicationObject: currentDeal.dealInfo.applicationInfo,
          });
          qa.answer = response.answer;
        }
        return undefined;
      }
      default:
        throw new Error(`No generator for ${item.id}`);
    }
  };

  const generateMutation = useMutation({
    // Optimistic spinner — set the pending flag synchronously on click so the
    // button never flashes back to "Run" between react-query's isPending
    // flipping false and the server's reportStatus catching up.
    onMutate: (item: AnalysisItem) => {
      markPending(item.id);
    },
    mutationFn: async (item: AnalysisItem) => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");

      // Activate the deal first if the report view gates behind
      // aiTracked — otherwise the panel would render the
      // "Active Deal Limit Reached" card when opened.
      const wasActivated = currentDeal.aiTracked === true;
      if (item.requiresActivation && !wasActivated) {
        const activation = await activateDeal(
          activeWorkspaceId,
          currentDeal.id
        );
        updateDeal({
          aiTracked: true,
          overview: {
            ...currentDeal.overview,
            status:
              activation.data.newStatus || currentDeal.overview.status,
          },
        });
        await fetchDealStats(activeWorkspaceId);
      }

      return triggerGeneration(item);
    },
    onSuccess: (_data, item) => {
      pendingOpenRef.current.add(item.id);
      // Remember the latest version id before this run so the auto-open waits
      // for the freshly-generated version instead of opening the previous one.
      genBaselineRef.current[item.id] = getVersions(item)[0]?._id;
      toast.success(`${item.title} generation started`);
      queryClient.invalidateQueries({
        queryKey: ["reportStatus", currentDeal.id, activeWorkspaceId],
      });
    },
    onError: (error: unknown, item) => {
      // Roll back the optimistic pending flag — the run never started.
      clearPending(item.id);
      toast.error(getErrorMessage(error, `Failed to generate ${item.title}`));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      item,
      versionId,
    }: {
      item: AnalysisItem;
      versionId: string;
    }) => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");
      if (item.id === "market-intel") {
        await aiApi.deleteMarketResearch({
          reportId: versionId,
          companyId: currentDeal.id,
          workspaceId: activeWorkspaceId,
        });
      } else if (item.id === "website-info") {
        await aiApi.deleteWebsiteSummaryV2({
          reportId: versionId,
          companyId: currentDeal.id,
          workspaceId: activeWorkspaceId,
        });
      } else if (item.id === "linkedin-info") {
        await aiApi.deleteLinkedInInfo({
          reportId: versionId,
          companyId: currentDeal.id,
          workspaceId: activeWorkspaceId,
        });
      } else {
        await aiApi.deleteAiGeneration({ generationId: versionId });
      }
      return { item, versionId };
    },
    onSuccess: ({ item, versionId }) => {
      const gen = currentDeal.aiGenerations as Record<string, unknown>;
      const arr = Array.isArray(gen[item.dataKey])
        ? (gen[item.dataKey] as Array<{ _id?: string }>)
        : [];
      const next = arr.filter((r) => r._id !== versionId);
      updateDeal({
        aiGenerations: {
          ...currentDeal.aiGenerations,
          [item.dataKey]: next,
        } as typeof currentDeal.aiGenerations,
      });
      setSelectedVersionByItem((prev) => {
        const copy = { ...prev };
        delete copy[item.id];
        return copy;
      });
      setDeleteTarget(null);
      toast.success(`${item.title} version deleted`);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete"));
      setDeleteTarget(null);
    },
  });

  // Running a premium report marks the deal premium (consumes a plan slot).
  const handleRun = (item: AnalysisItem) => {
    if (item.requiresActivation && currentDeal.aiTracked !== true) {
      const used = quota?.premium_deals_used;
      const cap = quota?.effective_premium_deals_cap ?? quota?.premium_deals_cap;
      if (used !== undefined && cap !== undefined) {
        // Over cap (grandfathered / post-downgrade): swap is a no-op since
        // demoting 1 still leaves us over. Block until they bring it under.
        if (used > cap) {
          toast.error(
            `Organization is over the premium cap (${used}/${cap}, shared across all workspaces). Demote at least ${used - cap + 1} premium deal${used - cap + 1 === 1 ? "" : "s"} before running new premium reports.`
          );
          return;
        }
        if (used === cap) {
          setSwapItem(item);
          return;
        }
      }
      setRunConfirmItem(item);
    } else {
      generateMutation.mutate(item);
    }
  };

  // Free up a slot for this deal, then generate the report the user clicked.
  // The swap already promoted this deal on the server, so generateMutation's
  // activateDeal call is a harmless re-activation (no extra slot consumed).
  const handleSwapConfirm = async (demoteApplicationId: string) => {
    const item = swapItem;
    if (!item || !activeWorkspaceId) return;
    setIsSwapping(true);
    try {
      await swapPremiumDeal(
        activeWorkspaceId,
        currentDeal.id,
        demoteApplicationId
      );
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      queryClient.invalidateQueries({ queryKey: ["orgPremiumDeals"] });
      setSwapItem(null);
      generateMutation.mutate(item);
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't free up a premium slot"));
    } finally {
      setIsSwapping(false);
    }
  };

  // Auto-open the panel when a user-triggered generation completes.
  useEffect(() => {
    const isActivated = currentDeal.aiTracked === true;
    for (const item of analysisItems) {
      if (!pendingOpenRef.current.has(item.id)) continue;
      if (item.requiresActivation && !isActivated) continue;
      if (isGenerating(item)) continue;
      const versions = getVersions(item);
      if (versions.length > 0) {
        // Versioned report: open only once the just-generated version (a new
        // latest id) has arrived, and open that version explicitly so the
        // panel doesn't stay on the previous one.
        const newestId = versions[0]?._id;
        if (newestId && newestId !== genBaselineRef.current[item.id]) {
          pendingOpenRef.current.delete(item.id);
          clearPending(item.id);
          onOpenInPanel(item.id, item.title, newestId);
        }
      } else {
        // No version dropdown (valuations) or override data (questionnaire) —
        // open as soon as the data is present.
        const present = item.hasDataOverride
          ? item.hasDataOverride(currentDeal)
          : !!getAnalysisData(item.dataKey);
        if (present) {
          pendingOpenRef.current.delete(item.id);
          clearPending(item.id);
          onOpenInPanel(item.id, item.title);
        }
      }
    }
    // Re-run whenever deal data or status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentDeal.aiGenerations,
    currentDeal.aiTracked,
    currentDeal.questionnaire,
    reportStatus,
  ]);

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <WebsiteAnalyticsCard currentDeal={currentDeal} />
      <div className="space-y-3">
        {analysisItems.map((item) => {
          const hasData = item.hasDataOverride
            ? item.hasDataOverride(currentDeal)
            : !!getAnalysisData(item.dataKey);
          // Existing reports stay viewable even after a deal is demoted to
          // non-premium (SAAS-798); only generating new ones stays gated.
          const canOpen = hasData;
          const generating = isGenerating(item);
          const mutating =
            generateMutation.isPending &&
            generateMutation.variables?.id === item.id;
          const pending = pendingItems.has(item.id) && !hasData;
          const showSpinner = generating || mutating || pending;
          const isQueued =
            generating &&
            !mutating &&
            getReportStatusValue(item) === "queued";
          const score = getScore(item.dataKey);
          const versions = canOpen ? getVersions(item) : [];
          const selectedVersionId =
            selectedVersionByItem[item.id] || versions[0]?._id || "";

          return (
            <div
              key={item.id}
              id={`report-${item.id}`}
              className="@container rounded-lg border border-border bg-card scroll-mt-24 transition-[box-shadow,background-color] duration-300 data-[flash=true]:bg-purple-50/60 data-[flash=true]:ring-2 data-[flash=true]:ring-purple-400 data-[flash=true]:ring-inset dark:data-[flash=true]:bg-purple-950/30"
            >
              <div className="flex flex-col gap-2 px-4 py-3 @sm:flex-row @sm:items-center @sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground">
                      {item.title}
                    </p>
                    {item.isPremium && (
                      <span className="text-[11px] text-muted-foreground/50">
                        Premium
                      </span>
                    )}
                  </div>
                  {canOpen && !showSpinner && !item.hideCompletedLabel ? (
                    <div className="mt-0.5">
                      {score !== null ? (
                        <ScoreBar
                          score={score.value}
                          maxScore={score.max}
                          format={score.format}
                          labels={score.labels}
                        />
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Completed
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {item.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                  {showSpinner && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {isQueued ? "Queued…" : "Generating…"}
                    </span>
                  )}

                  {canOpen && !showSpinner && (
                    <>
                      <VersionSelector
                        versions={versions}
                        selectedId={selectedVersionId}
                        onChange={(id) =>
                          setSelectedVersionByItem((prev) => ({
                            ...prev,
                            [item.id]: id,
                          }))
                        }
                      />
                      <button
                        className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
                        onClick={() =>
                          onOpenInPanel(
                            item.id,
                            item.title,
                            selectedVersionId || undefined
                          )
                        }
                      >
                        Open
                      </button>
                      {!item.skipVersionSelector && (
                        <ReportActionsMenu
                          disabled={deleteMutation.isPending}
                          onRegenerate={() => handleRun(item)}
                          onDelete={
                            selectedVersionId
                              ? () =>
                                  setDeleteTarget({
                                    item,
                                    versionId: selectedVersionId,
                                  })
                              : undefined
                          }
                        />
                      )}
                    </>
                  )}

                  {!canOpen &&
                    !showSpinner &&
                    (() => {
                      const blocker = item.getRunBlocker?.(currentDeal);
                      if (blocker) {
                        return (
                          <button
                            className="rounded-lg border border-border px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-foreground/[0.04]"
                            onClick={() =>
                              onNavigateToTab?.(
                                blocker.ctaTab,
                                blocker.ctaFieldId,
                              )
                            }
                          >
                            {blocker.ctaLabel}
                          </button>
                        );
                      }
                      return (
                        <button
                          className="rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-80 disabled:opacity-50"
                          disabled={!activeWorkspaceId}
                          onClick={() => handleRun(item)}
                        >
                          Run
                        </button>
                      );
                    })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.item.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this version of{" "}
              {deleteTarget?.item.title}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMutation.mutate(deleteTarget);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PremiumActivationDialog
        open={runConfirmItem !== null}
        onClose={() => setRunConfirmItem(null)}
        onConfirm={() => {
          if (runConfirmItem) generateMutation.mutate(runConfirmItem);
          setRunConfirmItem(null);
        }}
        reportName={runConfirmItem?.title ?? ""}
        nextStage={null}
        currentTracked={quota?.premium_deals_used ?? dealStats?.tracked ?? 0}
        showStatusChange={false}
      />

      <PremiumSwapModal
        open={swapItem !== null}
        onOpenChange={(open) => {
          if (!open) setSwapItem(null);
        }}
        orgId={orgId}
        newDealName={currentDeal.overview.name}
        isSubmitting={isSwapping}
        onConfirm={handleSwapConfirm}
      />
    </div>
  );
}

export default memo(AnalysisTab);
